import { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { DAY_ORDER_LONG, getMealPlanSettingsDefaults, DEFAULT_MEAL_SLOTS } from "../utils/mealPlanUtils";

const SHORT = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };

export function MealPlanSettingsModal({ open, onClose }) {
  const { mealPlanSettings, updateMealPlanSettings } = useHomeOSState();
  const [draft, setDraft] = useState(mealPlanSettings || getMealPlanSettingsDefaults());

  useEffect(() => {
    if (open) setDraft(mealPlanSettings || getMealPlanSettingsDefaults());
  }, [open, mealPlanSettings]);

  if (!open) return null;

  const addSlot = () => {
    setDraft(d => ({
      ...d,
      mealSlots: [
        ...d.mealSlots,
        { id: `slot-${Date.now()}`, label: "New slot", color: "amber", tile: "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-amber-800/60", text: "text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]", dot: "bg-transparent" },
      ],
    }));
  };
  const renameSlot = (idx, label) => {
    setDraft(d => ({ ...d, mealSlots: d.mealSlots.map((s, i) => i === idx ? { ...s, label } : s) }));
  };
  const deleteSlot = (idx) => {
    setDraft(d => {
      if (d.mealSlots.length <= 1) { toast.error("Keep at least one meal slot."); return d; }
      return { ...d, mealSlots: d.mealSlots.filter((_, i) => i !== idx) };
    });
  };
  const moveSlot = (idx, dir) => {
    setDraft(d => {
      const arr = [...d.mealSlots];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return d;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return { ...d, mealSlots: arr };
    });
  };
  const resetSlots = () => setDraft(d => ({ ...d, mealSlots: DEFAULT_MEAL_SLOTS.map(s => ({ ...s })) }));

  const save = () => {
    updateMealPlanSettings({
      weekStartDay: draft.weekStartDay,
      mealSlots: draft.mealSlots,
      showNutrition: !!draft.showNutrition,
      showAssignedCook: !!draft.showAssignedCook,
      showImages: !!draft.showImages,
    });
    toast.success("Meal plan settings saved.");
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="meal-plan-settings-modal">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home">
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur z-10 px-6 pt-5 pb-3 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50">Meal plan settings</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-400 flex items-center justify-center" data-testid="meal-plan-settings-close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Week start */}
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Week starts on</p>
            <div className="grid grid-cols-4 gap-1.5" data-testid="meal-plan-settings-week-start">
              {DAY_ORDER_LONG.map(d => {
                const code = SHORT[d];
                const active = draft.weekStartDay === code;
                return (
                  <button
                    key={d}
                    onClick={() => setDraft(s => ({ ...s, weekStartDay: code }))}
                    className={`px-2 py-1.5 rounded-full text-xs font-extrabold border-2 ${active ? "bg-transparent text-white border-rose-500" : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300"}`}
                    data-testid={`meal-plan-settings-start-${code}`}
                  >
                    {d.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Meal slots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Meal slots</p>
              <div className="flex items-center gap-1">
                <button onClick={resetSlots} className="text-[10px] font-extrabold text-gray-500 hover:underline" data-testid="meal-plan-settings-reset-slots">Reset</button>
                <button onClick={addSlot} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-transparent text-white text-[10px] font-extrabold" data-testid="meal-plan-settings-add-slot">
                  <Plus className="w-3 h-3" strokeWidth={3} /> Add slot
                </button>
              </div>
            </div>
            <ul className="space-y-1.5" data-testid="meal-plan-settings-slot-list">
              {draft.mealSlots.map((s, idx) => (
                <li key={s.id} className={`flex items-center gap-2 rounded-xl border ${s.tile} px-2 py-1.5`} data-testid={`meal-plan-settings-slot-${s.id}`}>
                  <input
                    value={s.label}
                    onChange={(e) => renameSlot(idx, e.target.value)}
                    className={`flex-1 bg-transparent text-sm font-extrabold ${s.text} focus:outline-none`}
                    data-testid={`meal-plan-settings-slot-label-${s.id}`}
                  />
                  <button onClick={() => moveSlot(idx, -1)} disabled={idx === 0} className="w-6 h-6 rounded-md text-gray-500 hover:bg-white/40 disabled:opacity-30" aria-label="Move up" data-testid={`meal-plan-settings-slot-up-${s.id}`}>
                    <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => moveSlot(idx, 1)} disabled={idx === draft.mealSlots.length - 1} className="w-6 h-6 rounded-md text-gray-500 hover:bg-white/40 disabled:opacity-30" aria-label="Move down" data-testid={`meal-plan-settings-slot-down-${s.id}`}>
                    <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => deleteSlot(idx)} className="w-6 h-6 rounded-md text-[color:var(--ara-ink-muted)] hover:bg-transparent" aria-label="Delete" data-testid={`meal-plan-settings-slot-delete-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Display toggles */}
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Display</p>
            <div className="space-y-1.5">
              <Toggle label="Show calories & macros" checked={!!draft.showNutrition} onChange={(v) => setDraft(s => ({ ...s, showNutrition: v }))} testId="meal-plan-settings-show-nutrition" />
              <Toggle label="Show assigned cook" checked={!!draft.showAssignedCook} onChange={(v) => setDraft(s => ({ ...s, showAssignedCook: v }))} testId="meal-plan-settings-show-cook" />
              <Toggle label="Show meal images" checked={!!draft.showImages} onChange={(v) => setDraft(s => ({ ...s, showImages: v }))} testId="meal-plan-settings-show-images" />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-6 py-3 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-full text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="meal-plan-settings-cancel">Cancel</button>
          <button onClick={save} className="ml-auto inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 text-white font-extrabold text-sm px-4 py-2 rounded-full shadow-md" data-testid="meal-plan-settings-save">
            <Save className="w-4 h-4" strokeWidth={1.5} /> Save settings
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-2 rounded-xl bg-gray-50 dark:bg-slate-800 px-3 py-2 cursor-pointer" data-testid={testId}>
      <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-rose-500" />
    </label>
  );
}

export default MealPlanSettingsModal;
